/**
 * Wd vehicle assignment service
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */

import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VehicleAssignmentFilterModel } from '../../../models/filters/vehicles/assignments/vehicle-assignment-filter.model';
import { isEmpty } from 'lodash';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';
import { ActivatedRoute } from '@angular/router';
import {
  IBcxErrorService,
  IBcxOauth2HttpService,
  IBcxSharedFunctionsService,
  IBcxStorageService,
} from '../../../interfaces/generic/generic-interfaces';
import {
  IVehicleAssignmentColumnOptions,
  IVehicleAssignmentFilteringOptions,
  IVehicleAssignmentFilterParams,
  IVehicleAssignmentModel,
  IVehicleAssignmentRequiredFiltersData,
} from '../../../interfaces/vehicles/vehicle-interfaces';
import { ManageAssignmentsComponent } from '../../../pages/shipping/components/drivers/manage/manage-assignments.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class WdVehicleAssignmentService {
  // Variables
  allVehicleAssignments = new BehaviorSubject<Array<object>>([]);
  allVehicleAssignmentListColumns: Array<string> = [];
  allVehicleAssignmentListColumnsOptions: Array<
    IVehicleAssignmentColumnOptions
  > = [];

  // Booleans
  isAddingVehicleAssignment = new BehaviorSubject(true); // Adding Single Driver Assignment
  isLoadingVehicleAssignments = new BehaviorSubject(true); // Loading Driver Assignments List
  isReloadingVehicleAssignments = new BehaviorSubject(false); // Reloading Driver Assignments List
  isUpdatingVehicleAssignment = new BehaviorSubject(false); // Updating Driver Assignments

  // Local Storage Key
  localStorageKey = 'bcxVehicleAssignment';

  // Vehicle assignment list Settings
  // Note: We save the last know total of each group/status/etc. in order limit what parameters we send in the
  // initial data load. Without it, we would have to send all parameters because we don't know which are selected.
  viewSettings: IVehicleAssignmentFilterParams = {
    page: 1,
    pageSize: 25,
    sortKey: 'dateStart',
    sortDir: 'desc',
    selected: { columns: [] },
  };

  // Vehicle assignment filters
  vehicleAssignmentFilters = new BehaviorSubject<
    IVehicleAssignmentFilteringOptions
  >({
    id: undefined,
    dateStart: undefined,
    dateEnd: undefined,
    driver: undefined,
    vehicle: undefined,
  });

  // Vehicle assignment required data
  vehicleAssignmentRequiredData = new BehaviorSubject<
    IVehicleAssignmentFilteringOptions
  >({
    userOptions: undefined,
    vehicleOptions: undefined,
  });

  // Current live filter values (for reference)
  currentLiveFilters: any = {
    dateStart: undefined,
    dateEnd: undefined,
    driver: undefined,
    vehicle: undefined,
    userOptions: undefined,
    vehicleOptions: undefined,
  };

  // Options
  vehicleAssignmentOptions: BehaviorSubject<
    Array<IVehicleAssignmentModel>
  > = new BehaviorSubject<Array<IVehicleAssignmentModel>>([]);

  /**
   * Constructor Initialization
   * @since 4.70.0
   * @memberof WdVehicleAssignmentService
   */
  constructor(
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdOauth2HttpService')
    public wdOauth2HttpService: IBcxOauth2HttpService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
    @Inject('wdStorageService') public wdStorageService: IBcxStorageService,
  ) {}

  /**
   * Add new vehicle assignment
   * @since 4.70.0
   */
  async addVehicleAssignment(inputData: any): Promise<void> {
    // Toggle Loader
    this.isAddingVehicleAssignment.next(true);
    try {
      // Format data for POST
      const data = WdVehicleAssignmentService.buildAssignmentApiData(inputData);
      // Add vehicle assignment
      await this.wdOauth2HttpService.post(
        `${this.wdLocalSharedFunctions.orderApiUrl}vehicles/assignments`,
        data,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyAdded', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Toggle Loader
      this.isAddingVehicleAssignment.next(false);
    }
  }

  /**
   * Delete vehicle assignment
   * @since 4.70.0
   */
  async deleteVehicleAssignment(element: any) {
    // Start loader
    element.isDeleting = new BehaviorSubject(true);
    try {
      // Await DELETE API call
      await this.wdOauth2HttpService.delete(
        `${this.wdLocalSharedFunctions.orderApiUrl}vehicles/assignments/${element?.id}`,
      );
      // Success
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyDeleted', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
      // Re-fetch vehicles
      await this.fetchVehicleAssignments(false, false);
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Stop loader
      element.isDeleting.next(false);
    }
  }

  /**
   * Get serialized vehicle assignment filters.
   */
  getSerializedVehicleAssignmentFilters(
    filters: BehaviorSubject<IVehicleAssignmentFilteringOptions>,
    paginate = true,
  ) {
    const filterModel = this.buildFilterModel(filters, paginate);
    return filterModel.getSerialized();
  }

  /**
   * Fetch vehicle assignment.
   */
  async fetchVehicleAssignments(
    showLoader = true,
    initFilters = false,
    paginate = true,
  ): Promise<Array<object>> {
    // Vehicle assignments list
    let vehicleAssignments: Array<any> = [];

    // Toggle Loader
    showLoader
      ? this.isLoadingVehicleAssignments.next(true)
      : this.isReloadingVehicleAssignments.next(true);
    try {
      // Set Filters
      const serializedFilters = this.getSerializedVehicleAssignmentFilters(
        this.vehicleAssignmentFilters,
        paginate,
      );
      const params = this.wdLocalSharedFunctions.buildFilters(
        serializedFilters,
      );

      // Set Path
      const path = 'vehicles/assignments/loadData';
      // Get Orders Data
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.dataExportApiUrl}${path}${
          params ? '?' + params : ''
        }`,
      );
      // Set Filters
      if (initFilters) {
        this.setFiltersData(response.data);
      }
      const rawOrderShippingVehicleAssignments = this.wdSharedFunctionsService.getDescendantProperty(
        response,
        'data.orderShippingVehicleAssignments',
      );
      // Return filtered vehicle assignments
      vehicleAssignments = this.parseVehicleAssignments(
        rawOrderShippingVehicleAssignments,
      );
      this.vehicleAssignmentOptions.next(vehicleAssignments);
      return vehicleAssignments;
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        this.translate.instant('outputs.failedToLoad', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
      return this.allVehicleAssignments.value;
    } finally {
      // Toggle Loader
      this.isLoadingVehicleAssignments.next(false);
      this.isReloadingVehicleAssignments.next(false);
      // Set vehicle assignments
      this.allVehicleAssignments.next(vehicleAssignments);
    }
  }

  /**
   * Fetch vehicle assignment by ID
   * @since 4.73.0
   */
  async fetchVehicleAssignmentManageData(
    id: string = null,
  ): Promise<IVehicleAssignmentModel> {
    // Toggle Loader

    try {
      // Set Filters
      const serializedFilters = this.getSerializedVehicleAssignmentFilters(
        this.vehicleAssignmentFilters,
      );
      let params = this.wdLocalSharedFunctions.buildFilters(serializedFilters);
      // Load just required data (or assignments data too if required)
      const requiredData = 'users,orderShippingVehicles';
      params += !id
        ? `&loadRequiredData=${requiredData}`
        : `&id=${id}&loadRequiredData=${requiredData},orderShippingVehicleAssignments`;

      // Set Path
      const path = 'vehicles/assignments/loadData';
      // Get Orders Data
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.dataExportApiUrl}${path}${
          params ? '?' + params : ''
        }`,
      );
      // Set Filters
      this.setFiltersData(response.data);
      const rawOrderShippingVehicleAssignments = this.wdSharedFunctionsService.getDescendantProperty(
        response,
        'data.orderShippingVehicleAssignments',
      );

      // Return filtered vehicle assignments
      return rawOrderShippingVehicleAssignments?.length > 0
        ? this.parseVehicleAssignment(rawOrderShippingVehicleAssignments[0])
        : null;
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        this.translate.instant('outputs.failedToLoad', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
      return null;
    } finally {
      // Toggle Loader
    }
  }

  /**
   * Update vehicle assignment
   */
  async updateVehicleAssignment(inputData: any): Promise<void> {
    // Toggle Loader
    this.isUpdatingVehicleAssignment.next(true);
    try {
      // Format data for PUT
      const data = WdVehicleAssignmentService.buildAssignmentApiData(inputData);
      // Update vehicle assignment
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}vehicles/assignments/${inputData?.id}`,
        data,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyUpdated', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Toggle Loader
      this.isUpdatingVehicleAssignment.next(false);
    }
  }

  /**
   * Update orders assigned to vehicle assignments.
   */
  async updateVehicleAssignmentOrders(data: {
    orderId: string;
    addToAssignments: Array<string>; // IDs of assignments where we need to add the order
    removeFromAssignments: Array<any>; // IDs of assignments where we need to remove the order
  }): Promise<void> {
    // Toggle Loader
    this.isUpdatingVehicleAssignment.next(true);
    try {
      // Update vehicle assignment
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}vehicles/assignments/orders`,
        data,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyUpdated', {
          type: this.translate.instant('shipping.driver.assignment.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      // throw error;
    } finally {
      // Toggle Loader
      this.isUpdatingVehicleAssignment.next(false);
    }
  }

  /**
   * Build API data for vehicle assignments.
   */
  private static buildAssignmentApiData(input: any) {
    const result: any = {};

    // Required fields
    result.dateStart = input.dateStart?.toISOString(); // Date Start
    result.dateEnd = input.dateEnd?.toISOString(); // Date End
    result.vehicleId = input.vehicle?.id;

    // Optional fields
    result.userId = input.driver?.id || null;
    result.notes = input.notes || null;
    if (input.orders) {
      result.orders = input.orders;
    }

    return result;
  }

  /**
   * Create instance of VehicleAssignmentFilterModel.
   */
  buildFilterModel(
    filters: BehaviorSubject<IVehicleAssignmentFilteringOptions>,
    paginate: boolean,
  ): VehicleAssignmentFilterModel {
    // Filters
    const { dateStart, dateEnd, driver, vehicle } = filters.value as any;
    // Settings
    const page = paginate ? this.viewSettings.page : null;
    const pageSize = paginate ? this.viewSettings.pageSize : null;
    const sortKey = this.viewSettings.sortKey;
    const sortDir = this.viewSettings.sortDir;
    // Vehicle assignment filter model
    return new VehicleAssignmentFilterModel(
      dateStart,
      dateEnd,
      page,
      pageSize,
      sortKey,
      sortDir,
      driver,
      vehicle,
    );
  }

  /**
   * Add vehicle assignment for order and date.
   */
  addOrderVehicleAssignment(orderId: string, date: Date): Promise<boolean> {
    const vehicleAssignment = {
      orders: [orderId],
      date: {
        start: date,
        end: date,
      },
    };
    return this.manageVehicleAssignment(vehicleAssignment);
  }

  /**
   * Open the manage vehicle assignment modal.
   */
  manageVehicleAssignment(element: any): Promise<boolean> {
    return new Promise((resolve) => {
      this.matDialog.open(ManageAssignmentsComponent, {
        disableClose: true,
        data: {
          assignment: element,
          onSuccessCallback: () => {
            resolve(true);
          },
        },
      });
    });
  }

  /**
   * Initialize local storage
   * @since 4.70.0
   */
  initializeLocalStorage() {
    // Local storage key
    const localStorageKey = this.localStorageKey;
    if (this.wdStorageService[localStorageKey]) {
      this.viewSettings = this.wdStorageService[localStorageKey];
    }
    this.wdStorageService.initializeDefaults(
      localStorageKey,
      this.viewSettings,
    );
    this.viewSettings = this.wdStorageService[localStorageKey];
    this.initializeRouteParams();
  }

  /**
   * Initialize view settings based on route query params
   * @since 4.70.0
   */
  initializeRouteParams() {
    this.route.queryParams.subscribe((params) => {
      if (params) {
        this.viewSettings.page = params.page
          ? parseInt(params.page)
          : this.viewSettings.page;
        this.viewSettings.pageSize = this.wdLocalSharedFunctions.pageSizeOptions.includes(
          parseInt(params.pageSize),
        )
          ? parseInt(params.pageSize)
          : this.viewSettings.pageSize;
        this.viewSettings.sortKey = params.sortKey
          ? params.sortKey
          : this.viewSettings.sortKey;
        this.viewSettings.sortDir = this.wdLocalSharedFunctions.pageSortDirectionOptions.includes(
          params.sortDir,
        )
          ? params.sortDir
          : this.viewSettings.sortDir;
        // Filter Parameters
        if (params.vehicleId) {
          this.vehicleAssignmentFilters.value.vehicle = {
            id: params.vehicleId,
          };
        }
      }
    });
  }

  /**
   * Initialize vehicle assignment column options
   */
  initVehicleAssignmentListColumnsOptions() {
    // Selected columns from local storage
    const savedColumns: Array<string> = this.viewSettings.selected.columns;
    // Initialize Translations
    this.translate.get('order.columns.toggle.title').subscribe(() => {
      // Init results
      const results: Array<any> = [];
      // Set Translations
      const options: Array<any> = [];

      options.push(
        {
          key: 'dateStart',
          label: this.translate.instant(
            'shipping.driver.assignment.dateStart.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'dateEnd',
          label: this.translate.instant(
            'shipping.driver.assignment.dateEnd.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'vehicle',
          label: this.translate.instant(
            'shipping.driver.assignment.vehicle.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'licence',
          label: this.translate.instant(
            'shipping.driver.assignment.licence.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'driverName',
          label: this.translate.instant(
            'shipping.driver.assignment.driverName.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'driverContact',
          label: this.translate.instant(
            'shipping.driver.assignment.driverContact.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'notes',
          label: this.translate.instant(
            'shipping.driver.assignment.notes.title',
          ),
          visible: true,
          canToggle: true,
        },
        {
          key: 'edit',
          label: this.translate.instant(
            'shipping.driver.assignment.edit.title',
          ),
          visible: true,
          canToggle: false,
          selected: true,
        },
        {
          key: 'delete',
          label: this.translate.instant(
            'shipping.driver.assignment.delete.title',
          ),
          visible: true,
          canToggle: false,
          selected: true,
        },
      );

      // Set options
      options.forEach((column: any) => {
        // Is column selected?
        column.selected =
          savedColumns.length > 0 ? savedColumns.includes(column.key) : true;
        // Is column visible
        if (column.visible) {
          results.push(column);
        }
      });
      // Set columns
      this.allVehicleAssignmentListColumnsOptions = results;
      // Set order list columns
      this.setVehicleAssignmentListColumns();
    });
  }

  /**
   * Parse filters
   * @since 4.70.0
   */
  parseFilters(key: string, values: Array<any>) {
    return values.map((item) => {
      // Users
      if (key === 'users') {
        item.fullName = [item.firstName, item.lastName].join(' ');
      }

      return item;
    });
  }

  /**
   * Parse single vehicle assignment
   * @since 4.70.0
   */
  parseVehicleAssignment(vehicleAssignment: any): IVehicleAssignmentModel {
    return vehicleAssignment;
  }

  /**
   * Parse multiple vehicle assignments
   * @since 4.70.0
   */
  parseVehicleAssignments(inputData: any): Array<any> {
    let results: any = [];

    if (inputData && inputData.length > 0) {
      results = inputData.map((order: any) => {
        return this.parseVehicleAssignment(order);
      });
    }

    return results;
  }

  /**
   * Save local storage settings
   */
  saveLocalStorageSettings() {
    this.wdStorageService.saveLocalStorageConfiguration(
      this.localStorageKey,
      this.viewSettings,
    );
  }

  /**
   * Save selected filters
   * @since 4.70.0
   */
  saveSelectedFilters() {
    // Save Local Storage Settings
    this.saveLocalStorageSettings();
  }

  /**
   * Set Filters Data
   * @since 4.70.0
   */
  setFiltersData(inputData?: IVehicleAssignmentRequiredFiltersData): void {
    let drivers: any = [];
    let vehicles: any = [];

    if (!isEmpty(inputData)) {
      if (!isEmpty(inputData.orderShippingVehicles)) {
        vehicles = this.parseFilters('users', inputData.orderShippingVehicles);
      }
      if (!isEmpty(inputData.users)) {
        drivers = this.parseFilters('users', inputData.users);
      }
    }

    // Set Filter Data
    this.vehicleAssignmentRequiredData.value.userOptions = drivers;
    this.vehicleAssignmentRequiredData.value.vehicleOptions = vehicles;
  }

  /**
   * Set vehicle assignment columns for list view
   * @since 4.70.0
   */
  setVehicleAssignmentListColumns() {
    // Init columns
    const results: Array<any> = [];

    // Add columns based on restrictions
    this.allVehicleAssignmentListColumnsOptions.forEach((column: any) => {
      // Column is toggled ON in settings
      const isColumnToggledOn = column.selected;
      switch (column.key) {
        default:
          if (isColumnToggledOn) {
            // Add standard columns
            results.push(column.key);
          }
      }
    });

    // Set columns
    this.allVehicleAssignmentListColumns = results;
    this.viewSettings.selected.columns = results;
    this.saveLocalStorageSettings();
  }
}
